import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions } from '../../actions/subscriptionActions';
import * as types from '../../constants/actionTypes';
import Routes from '../../constants/routes';
import { defaultLogoColors, MAX_NUM_FAQS, MAX_NUM_GOALGROUPS } from '../../constants/constants';
import Notification from '../Common/Notification';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import SubscriptionForm from '../Subscription/SubscriptionForm';
import PageContainer from '../Common/PageContainer';
import Card from '../Common/Card';
import { convertToRaw } from 'draft-js';
import { isValidCode } from '../../helpers/validation';

export class SubscriptionPage extends React.Component {
	constructor(props, context) {
		super(props, context);

		const newSub = {
			id: 0,
			name: '',
			accessCode: '',
			businessContactEmails: '',
			reportPageFooter: '',
			logoColor: defaultLogoColors.logoColor,
			highlightColor: defaultLogoColors.highlightColor,
			welcomeMessage: '',
			approvedMessage: '',
			emailMessage: '',
			isActive: true,
			isProjectWidgetActive: true,
			isProjectIdRequiredForLocationTask: false,
			isProjectWaiversActive: false,
			isProjectReferenceDatesActive: false,
			isGoalWidgetActive: true,
			isSSOAutoActivate: false,
			isSSOAutoCreate: false,
			isSSOEnabled: false,
			isWelcomeMessageActive: false,
			isWelcomeMessageConstant: false,
			isStartDateActive: false,
			isTaskDependenciesActive: false,
			isTaskStepsActive: false,
			isTaskEffortActive: false,
			isTaskFeedbackActive: false,
			isTaskFinancialsActive: false,
			isLanguageTranslationActive: false,
			isBaselineTaskValidationRulesActive: false,
			isWebhooksActive: false,
			isTrial: false,
			isSaveAndManageTaskFiltersActive: false,
			trialEndDate: '',
			defaultAgentUserId: '',
			billingPlanId: '',
			faqs: [
				{
					id: 0,
					question: '',
					answer: '',
					isActive: true,
					ordinal: 0,
				},
			],
			goalGroups: [
				{
					id: 0,
					subscriptionId: 0,
					name: '',
					isActive: true,
					ordinal: 0,
					goals: [],
				},
			],
		};

		this.state = {
			fileToUpload: null,
			isFormValid: true,
			showWelcomeMessageRequired: false,
			showApprovedMessageRequired: false,
			showUserInviteMessageRequired: false,
			newSub: newSub,
			subscription: newSub,
			imageBytes: '',
			isLoading: false,
			supportAgents: [],
			billingPlans: [],
			totalTasks: 0,
			isAccessCodeResponseValid: true,
			isAccessCodeFormValid: true,
		};

		this.changeLogo = this.changeLogo.bind(this);
		this.onClickSave = this.onClickSave.bind(this);
	}

	async componentDidMount() {
		document.title = 'Admin';
		await this.loadSubscription();
		await this.loadFilters();
	}

	loadSubscription = async () => {
		try {
			if (this.props.subscriptionId) {
				this.setState({ isLoading: true });
				const response = await this.props.actions.getSubscription(this.props.subscriptionId);
				if (response.type === types.GET_SUBSCRIPTION_SUCCESS) {
					// Ensure a blank FAQ exists if there are no FAQs on the Subscription
					let subscription = { ...this.state.subscription, ...response.payload.subscription };
					// convert from bizarre json serialization casing when all caps
					subscription.faqs = subscription.faQs.slice();
					if (!subscription.faqs || !subscription.faqs.length) {
						subscription.faqs = this.state.newSub.faqs;
					}
					subscription.goalGroups = response.payload.goalGroups;
					this.setState({
						subscription: subscription,
						imageBytes: response.payload.imageBytes,
						totalTasks: response.payload.totalTasks,
					});
				} else if (response.type === types.GET_SUBSCRIPTION_FAILURE) {
					Notification('error', 'Error', response.payload.message);
				}
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isLoading: false });
		}
	};

	loadFilters = async () => {
		try {
			this.setState({ isLoading: true });
			const response = await this.props.actions.getSubscriptionFilters(this.props.subscriptionId);
			if (response.type === types.GET_SUBSCRIPTION_FILTERS_SUCCESS) {
				this.setState({
					supportAgents: response.payload.supportAgents ? response.payload.supportAgents : [],
					billingPlans: response.payload.billingPlans,
				});
			} else if (response.type === types.GET_SUBSCRIPTION_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isLoading: false });
		}
	};

	addQuestion = () => {
		let faqs = this.state.subscription.faqs.slice();
		if (faqs.length < MAX_NUM_FAQS) {
			faqs.push({
				id: 0,
				question: '',
				answer: '',
				isActive: true,
				ordinal: faqs.length,
			});
			this.updateSubscription('faqs', faqs);
		}
	};

	removeQuestion = (index) => {
		let faqs = this.state.subscription.faqs.slice();
		faqs.splice(index, 1);
		if (faqs.length === 0) {
			faqs.push({
				id: 0,
				question: '',
				answer: '',
				isActive: true,
				ordinal: 0,
			});
		}
		this.updateSubscription('faqs', faqs);
	};

	removeGoalGroup = (index) => {
		let goalGroups = this.state.subscription.goalGroups.slice();
		goalGroups.splice(index, 1);
		if (goalGroups.length === 0) {
			goalGroups.push({
				id: 0,
				name: '',
				isActive: true,
				ordinal: 0,
				subscriptionId: this.state.subscription.id,
				goals: [],
			});
		}
		this.updateSubscription('goalGroups', goalGroups);
	};

	updateSubscription = (name, value) => {
		this.setState({ subscription: { ...this.state.subscription, [name]: value } });
	};

	onChange = (event) => {
		this.updateSubscription(event.currentTarget.name, event.currentTarget.value);
	};

	changeActive = (name, isChecked) => {
		if (name === 'isWelcomeMessageActive' && !isChecked) {
			this.setState({
				showWelcomeMessageRequired: false,
				subscription: { ...this.state.subscription, isWelcomeMessageActive: false },
			});
		} else if (name === 'isProjectWidgetActive' && !isChecked) {
			// Clear project related toggles
			this.setState({
				subscription: {
					...this.state.subscription,
					isProjectWidgetActive: false,
					isProjectIdRequiredForLocationTask: false,
					isProjectWaiversActive: false,
					isProjectReferenceDatesActive: false,
				},
			});
		} else if (name === 'isTrial' && !isChecked) {
			// Clear trial related toggle & data
			this.setState({
				subscription: {
					...this.state.subscription,
					isTrial: false,
					trialEndDate: '',
				},
			});
		} else {
			this.updateSubscription(name, isChecked);
		}
	};

	changeLogoColor = (hex) => {
		this.updateSubscription('logoColor', hex);
	};

	changeHighlightColor = (hex) => {
		this.updateSubscription('highlightColor', hex);
	};

	onWelcomeMessageChange = (editorState) => {
		const messageJson = convertToRaw(editorState.getCurrentContent());
		let isRequired = false;
		if (this.state.subscription.isWelcomeMessageActive && messageJson.blocks[0].text === '') {
			isRequired = true;
		}

		this.setState({
			showWelcomeMessageRequired: isRequired,
			subscription: { ...this.state.subscription, welcomeMessage: JSON.stringify(messageJson) },
		});
	};

	onApprovedMessageChange = (editorState) => {
		const messageJson = convertToRaw(editorState.getCurrentContent());
		let isRequired = false;
		if (messageJson.blocks[0].text === '') {
			isRequired = true;
		}

		this.setState({
			showApprovedMessageRequired: isRequired,
			subscription: { ...this.state.subscription, approvedMessage: JSON.stringify(messageJson) },
		});
	};

	onEmailMessageChange = (editorState) => {
		const messageJson = convertToRaw(editorState.getCurrentContent());
		let isRequired = false;
		if (messageJson.blocks[0].text === '') {
			isRequired = true;
		}

		this.setState({
			showUserInviteMessageRequired: isRequired,
			subscription: { ...this.state.subscription, emailMessage: JSON.stringify(messageJson) },
		});
	};

	changeLogo = (file) => {
		this.setState({ fileToUpload: file });
	};

	validateForm = () => {
		let isFormValid = true;
		let isShowWelcomeMessageRequired = false;
		let isShowApprovedMessageRequired = false;
		let isShowUserInviteMessageRequired = false;

		const name = this.state.subscription.name;
		const isValidName = name && name.trim().length > 0;
		const accessCode = this.state.subscription.accessCode;
		const isAccessCodeFormValid = accessCode && accessCode.trim().length > 0 && isValidCode(accessCode);
		if (!isValidName || !isAccessCodeFormValid) {
			isFormValid = false;
		}

		if (!this.state.subscription.billingPlanId) {
			isFormValid = false;
		}

		if (this.state.subscription.isWelcomeMessageActive) {
			const welcomeMessage = this.state.subscription.welcomeMessage;

			if (!welcomeMessage) {
				isShowWelcomeMessageRequired = true;
				isFormValid = false;
			} else {
				const welcomeMessageJson = JSON.parse(this.state.subscription.welcomeMessage);
				if (welcomeMessageJson.blocks[0].text === '') {
					isShowWelcomeMessageRequired = true;
					isFormValid = false;
				}
			}
		}

		if (!this.state.subscription.approvedMessage) {
			isShowApprovedMessageRequired = true;
			isFormValid = false;
		} else {
			const approvedMessageJson = JSON.parse(this.state.subscription.approvedMessage);
			if (approvedMessageJson.blocks[0].text === '') {
				isShowApprovedMessageRequired = true;
				isFormValid = false;
			}
		}

		if (!this.state.subscription.emailMessage) {
			isShowUserInviteMessageRequired = true;
			isFormValid = false;
		} else {
			const emailMessageJson = JSON.parse(this.state.subscription.emailMessage);
			if (emailMessageJson.blocks[0].text === '') {
				isShowUserInviteMessageRequired = true;
				isFormValid = false;
			}
		}

		this.setState({
			isFormValid: isFormValid,
			isAccessCodeFormValid: isAccessCodeFormValid,
			isAccessCodeResponseValid: true,
			showWelcomeMessageRequired: isShowWelcomeMessageRequired,
			showApprovedMessageRequired: isShowApprovedMessageRequired,
			showUserInviteMessageRequired: isShowUserInviteMessageRequired,
		});
		return isFormValid;
	};

	onClickSave = async (event) => {
		event.preventDefault();
		event.stopPropagation();

		try {
			if (this.validateForm()) {
				this.setState({ isLoading: true });
				let subscription = this.state.subscription;

				// remove all blank faqs and goal groups
				subscription.faqs = subscription.faqs.filter(
					(x) => x.question && x.question.trim().length > 0 && x.answer && x.answer.trim().length > 0
				);
				subscription.goalGroups = subscription.goalGroups.filter((x) => x.name && x.name.trim().length > 0);

				const result = await this.props.actions.saveSubscription(subscription, this.state.fileToUpload);
				if (result.type === types.CREATE_SUBSCRIPTION_SUCCESS) {
					Notification('success', 'Create Successful', 'Your new subscription has been created.');
					this.props.history.push(Routes.DashboardEhAdmin);
				} else if (result.type === types.UPDATE_SUBSCRIPTION_SUCCESS) {
					Notification('success', 'Update Successful', 'Your subscription has been updated.');
					this.props.history.push(Routes.DashboardEhAdmin);
				} else if (result.type === types.CREATE_SUBSCRIPTION_FAILURE || result.type === types.UPDATE_SUBSCRIPTION_FAILURE) {
					if (!result.payload.IsValidAccessCode) {
						this.setState({ isAccessCodeResponseValid: result.payload.IsValidAccessCode });
					}
					Notification('error', 'Error', result.payload.message);
					this.setState({ isLoading: false });
				}
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.setState({ isLoading: false });
		} finally {
			// Do not set isLoading state here. If the save was successful, the
			// component is unloaded already because of the redirect
		}
	};

	onCancel = () => {
		event.preventDefault();
		event.stopPropagation();
		this.props.history.push(Routes.DashboardEhAdmin);
	};

	render() {
		return (
			<PageContainer>
				<Card title='Subscription Profile'>
					<LoadingIndicator isLoading={this.state.isLoading} hideWhileLoading={true}>
						<SubscriptionForm
							subscription={this.state.subscription}
							supportAgents={this.state.supportAgents}
							billingPlans={this.state.billingPlans}
							totalTasks={this.state.totalTasks}
							image={this.state.imageBytes}
							addQuestion={this.addQuestion}
							removeQuestion={this.removeQuestion}
							removeGoalGroup={this.removeGoalGroup}
							updateSubscription={this.updateSubscription}
							maxNumFAQs={MAX_NUM_FAQS}
							maxNumGoalGroups={MAX_NUM_GOALGROUPS}
							onSubmitForm={this.onClickSave}
							onCancelForm={this.onCancel}
							onInputChange={this.onChange}
							onActiveChange={this.changeActive}
							onLogoColorChange={this.changeLogoColor}
							onHighlightColorChange={this.changeHighlightColor}
							onLogoChange={this.changeLogo}
							onWelcomeMessageChange={this.onWelcomeMessageChange}
							onApprovedMessageChange={this.onApprovedMessageChange}
							onEmailMessageChange={this.onEmailMessageChange}
							isAccessCodeResponseValid={this.state.isAccessCodeResponseValid}
							isAccessCodeFormValid={this.state.isAccessCodeFormValid}
							isFormValid={this.state.isFormValid}
							showWelcomeMessageRequired={this.state.showWelcomeMessageRequired}
							showApprovedMessageRequired={this.state.showApprovedMessageRequired}
							showUserInviteMessageRequired={this.state.showUserInviteMessageRequired}
						/>
					</LoadingIndicator>
				</Card>
			</PageContainer>
		);
	}
}
SubscriptionPage.propTypes = {
	history: PropTypes.object.isRequired,
	subscriptionId: PropTypes.string,
	actions: PropTypes.object.isRequired,
};

function mapStateToProps(state, ownProps) {
	let subscriptionId = null;
	if (ownProps.match.params.subscriptionId) {
		subscriptionId = ownProps.match.params.subscriptionId;
	}

	return {
		subscriptionId: subscriptionId,
		history: ownProps.history,
	};
}

function mapDispatchToProps(dispatch) {
	return {
		actions: bindActionCreators(actions, dispatch),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(SubscriptionPage);
